import React, { useMemo, useState, useCallback } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FeedbackCommentProps, RenderQuestionProps } from '../utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { indicatorSectionsInOrder } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import ImpactDetailStageIcons, {
  CommentDetails,
  CommentsByStage,
  ImpactDetailStageIconsProps,
  parseFeedbackCommentsWithDetails,
} from './ImpactDetailStageIcons';
import TrueImpactFileRender from 'shared/design-system/components/TrueImpactFileRender';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import JSON5 from 'json5';
import { numberWithCommas } from 'shared/utils/numberUtils';
import {
  indicatorFileDescriptionInitialState,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUploadRender';

export type CommentsByStageIndicatorDetails = CommentsByStage<
  IndicatorDetails,
  IndicatorDetails['indicatorId']
>;

export const parseImpactDetailsAnswer = (
  text: string | undefined,
): SectionIndicators | undefined => {
  if (!text) return undefined;
  try {
    return JSON5.parse(text);
  } catch (e) {
    return undefined;
  }
};

export const parseCommentDetailsWithIndicatorDetails = (
  details: string | undefined,
): CommentDetails<IndicatorDetails> | undefined => {
  if (!details) return undefined;
  try {
    const d: CommentDetails<IndicatorDetails> = JSON.parse(details);
    if (d.indicatorId && d.sectionKey && d.subQuestion) return d;
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const indicatorDetailsKeys = (): (keyof IndicatorDetails)[] => [
  'forecast',
  'measurement',
  'file',
];

const indicatorNameStyle: React.CSSProperties = { marginBottom: SPACERS.tiny };
export const getIndicatorDetailsItemTitle = (
  indicator: IndicatorDetails,
): React.ReactNode => (
  <div style={indicatorNameStyle}>
    <strong>Indicator Name: </strong>
    {indicator.indicatorName}
  </div>
);

export const getIndicatorDetailsId = (indicator: IndicatorDetails): number =>
  indicator.indicatorId;

export const getIndicatorDetailsTitle = (
  key: keyof IndicatorDetails,
  indicator: IndicatorDetails,
): string => {
  switch (key) {
    case 'forecast':
      return 'Forecasted Indicator';
    case 'measurement':
      return 'Measurement type';
    case 'file':
      return 'Sources or assumptions';
    default:
      return 'Others';
  }
};

export const getIndicatorDetailsDescription = (
  key: keyof IndicatorDetails,
  indicator: IndicatorDetails,
): string | number | undefined => {
  switch (key) {
    case 'forecast':
      return `${numberWithCommas(indicator.forecast)} ${
        indicator.indicatorName
      }`;
    case 'measurement':
      return indicator.measurement;
    case 'file':
      return indicatorFileDescriptionInitialState(indicator);
    default:
      return indicator[key] as string;
  }
};

const style = { marginTop: SPACERS.tiny };
export const getExtraIndicatorDetails = (
  key: keyof IndicatorDetails,
  indicator: IndicatorDetails,
): React.ReactNode => {
  switch (key) {
    case 'file':
      return <TrueImpactFileRender style={style} file={indicator.file} />;
    default:
      return null;
  }
};

const FeedbackImpactDetails: React.FC<RenderQuestionProps> = ({
  reportId,
  questionId,
  title,
  answer,
  comments,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  commentsLength,
  className,
  addOrUpdateComment,
}) => {
  const indicators = useMemo(() => parseImpactDetailsAnswer(answer.text), [
    answer.text,
  ]);
  const [commentsByStage, setCommentsByStage] = useState<
    CommentsByStageIndicatorDetails
  >(
    parseFeedbackCommentsWithDetails(
      comments,
      parseCommentDetailsWithIndicatorDetails,
    ),
  );

  const getComments: ImpactDetailStageIconsProps<
    IndicatorDetails
  >['getComments'] = useCallback(
    ({ sectionKey, indicatorId, subQuestion }): FeedbackCommentProps[] =>
      commentsByStage[sectionKey]?.[indicatorId]?.[subQuestion] || [],
    [commentsByStage],
  );

  if (!indicators) {
    return (
      <TrueImpactText
        variant="body-bold"
        color="critical"
        className={className}
      >
        Indicators details were not saved properly. Please contact support about
        this report.
      </TrueImpactText>
    );
  }

  return (
    <div className={className}>
      <TrueImpactText variant="body">
        <strong>Indicator Summary</strong>
        <br />
        These are the indicator definitions provided by grantees for the
        indicators in their Impact Model. You can leave feedback on each
        indicator.
      </TrueImpactText>
      {indicatorSectionsInOrder.map(key => (
        <ImpactDetailStageIcons<IndicatorDetails>
          itemTitle={getIndicatorDetailsItemTitle}
          reportId={reportId}
          questionId={questionId}
          currentUserName={currentUserName}
          currentUserId={currentUserId}
          currentUserType={currentUserType}
          currentUserEmail={currentUserEmail}
          sectionId="indicator_details"
          sectionKey={key}
          options={indicators[key]}
          getIndicatorId={getIndicatorDetailsId}
          getTitle={getIndicatorDetailsTitle}
          getComments={getComments}
          getDescription={getIndicatorDetailsDescription}
          getExtraDetails={getExtraIndicatorDetails}
          setCommentsByStage={setCommentsByStage}
          getKeys={indicatorDetailsKeys}
        />
      ))}
    </div>
  );
};

export default FeedbackImpactDetails;
